import React from 'react';
import { Form, Button, Badge, Alert, Spinner, Row, Col } from 'react-bootstrap';
import FilterGroup from './FilterGroup';
import { getValidationMessage } from '../utils/analyticsUtils';

const QueryConfiguration = ({
  query,
  selectedTable,
  selectedTableInfo,
  loading,
  dropdownOptions,
  clientNames,
  onInputChange,
  onAddFilter,
  onAddFilterGroup,
  onUpdateFilter,
  onRemoveFilter,
  onUpdateGroupLogicalOperator,
  onExecuteQuery,
  onResetAll,
  onOpenClientModal,
  onHandleClientIdChange,
  getSelectedClientName,
  getFilterColumnInfo
}) => {
  const validationMessage = getValidationMessage(loading, selectedTable, query.filterGroups);

  return (
    <Form>
      <MetricsSection
        query={query}
        selectedTable={selectedTable}
        selectedTableInfo={selectedTableInfo}
        onInputChange={onInputChange}
      />

      <GroupingSection
        query={query}
        selectedTable={selectedTable}
        onInputChange={onInputChange}
      />

      <DateRangeSection
        query={query}
        selectedTable={selectedTable}
        onInputChange={onInputChange}
      />

      <SortingSection
        query={query}
        selectedTable={selectedTable}
        onInputChange={onInputChange}
      />

      <FiltersSection
        query={query}
        selectedTable={selectedTable}
        selectedTableInfo={selectedTableInfo}
        dropdownOptions={dropdownOptions}
        clientNames={clientNames}
        onAddFilter={onAddFilter}
        onAddFilterGroup={onAddFilterGroup}
        onUpdateFilter={onUpdateFilter}
        onRemoveFilter={onRemoveFilter}
        onUpdateGroupLogicalOperator={onUpdateGroupLogicalOperator}
        onOpenClientModal={onOpenClientModal}
        onHandleClientIdChange={onHandleClientIdChange}
        getSelectedClientName={getSelectedClientName}
        getFilterColumnInfo={getFilterColumnInfo}
      />

      <ActionButtons
        loading={loading}
        selectedTable={selectedTable}
        query={query}
        onExecuteQuery={onExecuteQuery}
        onResetAll={onResetAll}
      />

      {validationMessage && (
        <Alert variant="warning" className="mt-3 mb-0">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {validationMessage}
        </Alert>
      )}
    </Form>
  );
};

const MetricsSection = ({ query, selectedTable, selectedTableInfo, onInputChange }) => (
  <div className="mb-4">
    <h6 className="text-muted mb-3">Métrica</h6>
    <Form.Group className="mb-3">
      <Form.Label>Columna</Form.Label>
      <Form.Select
        value={query.metricColumn}
        onChange={(e) => onInputChange('metricColumn', e.target.value)}
        disabled={!selectedTable}
        required
      >
        {(selectedTableInfo?.columns?.metrics || []).map(column => (
          <option key={column.name} value={column.name}>
            {column.displayName || column.name}
          </option>
        ))}
      </Form.Select>
    </Form.Group>
  </div>
);

const GroupingSection = ({ query, selectedTable, onInputChange }) => (
  <div className="mb-4">
    <h6 className="text-muted mb-3">Agrupación</h6>
    <Row>
      <Col>
        <Form.Group className="mb-3">
          <Form.Label>Agrupar por</Form.Label>
          <Form.Select 
            value={query.dimension} 
            onChange={(e) => onInputChange('dimension', parseInt(e.target.value))}
            disabled={!selectedTable}
          >
            <option value={0}>Sin agrupar</option>
            <option value={1}>Comercial</option>
            <option value={2}>Transportista</option>
            <option value={3}>Cliente</option>
            <option value={4}>Producto</option>
            <option value={5}>Categoría de Producto</option>
            <option value={6}>Franja Horaria</option>
            <option value={7}>Tipo de Incidencia</option>
            <option value={8}>Acción de Incidencia</option>
            <option value={9}>Origen del Cliente</option>
            <option value={10}>Método de Pago</option>
            <option value={11}>Código Postal</option>
          </Form.Select>
        </Form.Group>
      </Col>
      <Col>
        <Form.Group className="mb-3">
          <Form.Label>Marco Temporal</Form.Label>
          <Form.Select 
            value={query.timeFrame} 
            onChange={(e) => onInputChange('timeFrame', parseInt(e.target.value))}
            disabled={!selectedTable}
          >
            <option value={0}>Ninguno</option>
            <option value={1}>Diario</option>
            <option value={2}>Semanal</option>
            <option value={3}>Mensual</option>
            <option value={4}>Trimestral</option>
            <option value={5}>Anual</option>
          </Form.Select>
        </Form.Group>
      </Col>
    </Row>
  </div>
);

const DateRangeSection = ({ query, selectedTable, onInputChange }) => (
  <div className="mb-4">
    <h6 className="text-muted mb-3">Rango de Fechas</h6>
    <Row>
      <Col>
        <Form.Group className="mb-3">
          <Form.Label>Filtrar por Tipo de Fecha</Form.Label>
          <Form.Select
            value={query.dateColumn}
            onChange={(e) => onInputChange('dateColumn', parseInt(e.target.value))}
            disabled={!selectedTable}
          >
            <option value={0}>Fecha de Pedido</option>
            <option value={1}>Fecha de Entrega</option>
            <option value={2}>Fecha de Firma</option>
            <option value={3}>Fecha de Entregado</option>
          </Form.Select>
        </Form.Group>
      </Col>
    </Row>
    <Row>
      <Col>
        <Form.Group className="mb-3">
          <Form.Label>Fecha Inicio</Form.Label>
          <Form.Control
            type="date"
            value={query.startDate}
            onChange={(e) => onInputChange('startDate', e.target.value)}
            disabled={!selectedTable}
          />
        </Form.Group>
      </Col>
      <Col>
        <Form.Group className="mb-3">
          <Form.Label>Fecha Fin</Form.Label>
          <Form.Control
            type="date"
            value={query.endDate}
            onChange={(e) => onInputChange('endDate', e.target.value)}
            disabled={!selectedTable}
          />
        </Form.Group>
      </Col>
    </Row>
  </div>
);

const SortingSection = ({ query, selectedTable, onInputChange }) => (
  <div className="mb-4">
    <h6 className="text-muted mb-3">Ordenamiento</h6>
    <Row>
      <Col>
        <Form.Group className="mb-3">
          <Form.Label>Ordenar Resultados</Form.Label>
          <Form.Select 
            value={query.orderBy} 
            onChange={(e) => onInputChange('orderBy', parseInt(e.target.value))}
            disabled={!selectedTable}
          >
            <option value={0}>Ascendente</option>
            <option value={1}>Descendente</option>
          </Form.Select>
        </Form.Group>
      </Col>
      {query.timeFrame > 0 && (
        <Col>
          <Form.Group className="mb-3">
            <Form.Label>Ordenar Marco Temporal</Form.Label>
            <Form.Select 
              value={query.timeFrameOrderBy} 
              onChange={(e) => onInputChange('timeFrameOrderBy', parseInt(e.target.value))}
              disabled={!selectedTable}
            >
              <option value={0}>Ascendente</option>
              <option value={1}>Descendente</option>
            </Form.Select>
          </Form.Group>
        </Col>
      )}
    </Row>
  </div>
);

const FiltersSection = ({ 
  query, 
  selectedTable, 
  selectedTableInfo,
  dropdownOptions,
  clientNames,
  onAddFilter, 
  onAddFilterGroup,
  onUpdateFilter,
  onRemoveFilter,
  onUpdateGroupLogicalOperator,
  onOpenClientModal,
  onHandleClientIdChange,
  getSelectedClientName,
  getFilterColumnInfo
}) => (
  <div className="mb-4">
    <div className="d-flex justify-content-between align-items-center mb-3">
      <h6 className="text-muted mb-0">
        Filtros {query.filterGroups.reduce((total, group) => total + group.filters.length, 0) > 0 && 
        <Badge bg="secondary" className="ms-2">
          {query.filterGroups.reduce((total, group) => total + group.filters.length, 0)}
        </Badge>}
      </h6>
      <div>
        <Button 
          variant="outline-primary" 
          size="sm" 
          onClick={() => onAddFilter(0)}
          disabled={!selectedTable}
          className="me-2"
        >
          + Filtro
        </Button>
        <Button 
          variant="outline-secondary" 
          size="sm" 
          onClick={onAddFilterGroup}
          disabled={!selectedTable}
        >
          + Grupo
        </Button>
      </div>
    </div>
    
    <FilterGroup
      filterGroups={query.filterGroups}
      selectedTableInfo={selectedTableInfo}
      dropdownOptions={dropdownOptions}
      clientNames={clientNames}
      onUpdateFilter={onUpdateFilter}
      onRemoveFilter={onRemoveFilter}
      onUpdateGroupLogicalOperator={onUpdateGroupLogicalOperator}
      onAddFilter={onAddFilter}
      onOpenClientModal={onOpenClientModal}
      onHandleClientIdChange={onHandleClientIdChange}
      getSelectedClientName={getSelectedClientName}
      getFilterColumnInfo={getFilterColumnInfo}
    />
  </div>
);

const ActionButtons = ({ loading, selectedTable, query, onExecuteQuery, onResetAll }) => {
  const validationMessage = getValidationMessage(loading, selectedTable, query.filterGroups);
  
  return (
    <>
      <Button 
        variant="primary" 
        onClick={() => onExecuteQuery(1)} 
        disabled={loading || !selectedTable || validationMessage}
        className="w-100 py-2"
      >
      {loading ? (
        <>
          <Spinner animation="border" size="sm" className="me-2" />
          Procesando...
        </>
      ) : (
        'Ejecutar Consulta'
      )}
    </Button>
    
    <div className="mt-4 pt-3 border-top">
      <Button 
        variant="outline-danger" 
        onClick={onResetAll}
        disabled={loading}
        size="sm"
        className="w-100"
      >
        🔄 Reiniciar Todo
      </Button>
    </div>
  </>
  );
};

export default QueryConfiguration;